import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { AppHeader } from '@/components/AppHeader';
import { PlayerNameLink } from '@/components/PlayerNameLink';
import { getPublicPairings, getPublicTournament } from '@/lib/api';
import { usePolledData } from '@/lib/usePolledData';
import { viewerHighlight } from '@/lib/viewerRow';
import { formatStartedAgo } from '@/lib/relativeTime';
import type { TFunction } from 'i18next';

// Translate the canonical color names from the game config
// ("White" / "Black" / "Player 1" / "Player 2") via slugified
// keys under tournament.pairings_public.color.*. Falls through to
// the raw English name on a miss so unknown values still render.
function colorLabel(canonical: string, t: TFunction): string {
  const slug = canonical.toLowerCase().replace(/\s+/g, '_');
  const key = `pairings_public.color.${slug}`;
  const translated = t(key);
  return translated === key ? canonical : translated;
}
import { useAuth } from '@/contexts/AuthContext';
import type { PublicPairingsResponse, Tournament } from '@swiss-magic/shared-types';
import { getGameConfig } from '@swiss-magic/shared-types';

function playerName(p: { firstName: string; lastName: string }): string {
  return `${p.firstName} ${p.lastName}`.trim();
}

export function PairingsPublicPage() {
  const { t } = useTranslation('tournament');
  const { slug, roundNumber: roundParam } = useParams<{
    slug: string;
    roundNumber: string;
  }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const roundNumber = Number(roundParam);
  const { data, message, loading } = usePolledData<PublicPairingsResponse>(
    () => getPublicPairings(slug!, roundNumber),
    [slug, roundNumber],
  );
  // One-shot fetch — see StandingsPage for the rationale.
  const [tournament, setTournament] = useState<Tournament | null>(null);
  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    getPublicTournament(slug)
      .then((t) => {
        if (!cancelled) setTournament(t);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p>{t('loading')}</p>
      </div>
    );
  }

  const tournamentName = data?.tournamentName ?? tournament?.name;

  return (
    <div data-testid="pairings-public-page" className="min-h-screen bg-background">
      <AppHeader />

      <main className="mx-auto max-w-4xl px-4 md:px-6 py-6">
        <div className="mb-6">
          {tournamentName && (
            <h2 className="text-xl md:text-2xl font-semibold mb-1">
              {tournamentName}
            </h2>
          )}
          <p className="text-sm text-muted-foreground mb-3">
            {t('common.round_n_label', { roundNumber: data?.roundNumber ?? roundNumber })}
          </p>
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            <Link
              to={`/t/${slug}`}
              className="text-sm text-muted-foreground underline underline-offset-4 hover:text-foreground"
            >
              {t('common.section_labels.info')}
            </Link>
            <Link
              to={`/t/${slug}/standings`}
              data-testid="standings-link"
              className="text-sm text-muted-foreground underline underline-offset-4 hover:text-foreground"
            >
              {t('common.section_labels.standings')}
            </Link>
            <Link
              to={`/t/${slug}/crosstable`}
              data-testid="crosstable-link"
              className="text-sm text-muted-foreground underline underline-offset-4 hover:text-foreground"
            >
              {t('common.section_labels.crosstable')}
            </Link>
          </div>
        </div>

        {data && (
          <div className="mb-4 flex flex-wrap items-center gap-1">
            <span className="text-sm font-medium text-muted-foreground mr-1">{t('common.round_tab_label')}</span>
            {Array.from({ length: data.totalPublishedRounds }, (_, i) => i + 1).map(
              (rn) => (
                <Button
                  key={rn}
                  variant={rn === roundNumber ? 'default' : 'outline'}
                  size="sm"
                  className="h-7 min-w-7 px-1.5 text-xs"
                  data-testid={`round-nav-${rn}`}
                  onClick={() => navigate(`/t/${slug}/pairings/${rn}`)}
                >
                  {rn}
                </Button>
              ),
            )}
          </div>
        )}

        {message && (
          <p data-testid="pairings-message" className="text-center text-muted-foreground">
            {message}
          </p>
        )}

        {data && (() => {
          const gameConfig = getGameConfig(data.game);
          return (
          <div className="rounded border">
            <table data-testid="pairings-public-table" className="w-full text-left">
              <thead className="border-b bg-muted/50">
                <tr>
                  <th className="w-8 px-1.5 py-2 text-xs font-medium text-muted-foreground">{t('pairings_public.table.table_number')}</th>
                  <th className="px-1.5 py-2 text-xs font-medium text-muted-foreground">{colorLabel(gameConfig.colorNames.first, t)}</th>
                  <th className="w-14 px-1 py-2 text-center text-xs font-medium text-muted-foreground">{t('pairings_public.table.result')}</th>
                  <th className="px-1.5 py-2 text-xs font-medium text-muted-foreground">{colorLabel(gameConfig.colorNames.second, t)}</th>
                </tr>
              </thead>
              <tbody>
                {data.pairings.map((pairing) => {
                  const isBye = pairing.blackPlayer === null;
                  const isConfirmed = pairing.resultStatus === 'confirmed';
                  const whiteWon = isConfirmed && (pairing.result === '1-0' || pairing.result === '+/-');
                  const blackWon = isConfirmed && (pairing.result === '0-1' || pairing.result === '-/+');
                  // Pairings don't bold the viewer's row — bold is already
                  // reserved for the match winner. Tint only.
                  const mineWhite = viewerHighlight(pairing.whitePlayer.profileUserId, user?.id, { bold: false });
                  const mineBlack = viewerHighlight(pairing.blackPlayer?.profileUserId, user?.id, { bold: false });
                  const mine = mineWhite.isMine ? mineWhite : mineBlack;
                  return (
                    <tr
                      key={pairing.tableNumber}
                      data-testid={`public-pairing-row-${pairing.tableNumber}`}
                      data-viewer-row={mine.isMine ? 'true' : undefined}
                      className={`border-b hover:bg-muted/30 ${!isConfirmed && !isBye ? 'bg-muted/10' : ''} ${mine.className}`}
                    >
                      <td className="px-1.5 py-2 text-xs text-muted-foreground">
                        {pairing.tableNumber}
                      </td>
                      <td className={`px-1.5 py-2 text-sm ${whiteWon ? 'font-semibold' : ''}`}>
                        <PlayerNameLink
                          slug={slug!}
                          startingRank={pairing.whitePlayer.startingRank}
                          name={playerName(pairing.whitePlayer)}
                        />
                      </td>
                      <td className="px-1 py-2 text-center text-sm font-mono font-semibold">
                        {isBye ? (
                          <span className="text-xs text-muted-foreground">{t('pairings_public.bye_label')}</span>
                        ) : isConfirmed ? (
                          pairing.result
                        ) : pairing.startedAt ? (
                          <span
                            data-testid={`public-pairing-in-progress-${pairing.tableNumber}`}
                            className="rounded bg-blue-600 px-1.5 py-0.5 text-xs font-normal text-white"
                            title={formatStartedAgo(pairing.startedAt)}
                          >
                            {t('pairings_public.result_status.in_progress')}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">{t('pairings_public.round_dash')}</span>
                        )}
                      </td>
                      <td className={`px-1.5 py-2 text-sm ${blackWon ? 'font-semibold' : ''}`}>
                        {isBye ? (
                          <span className="text-muted-foreground italic text-xs">{t('pairings_public.bye_lowercase')}</span>
                        ) : (
                          <PlayerNameLink
                            slug={slug!}
                            startingRank={pairing.blackPlayer!.startingRank}
                            name={playerName(pairing.blackPlayer!)}
                          />
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          );
        })()}
      </main>
    </div>
  );
}
